import React, { useCallback, useMemo } from "react";
import { VsiBubble, VsiBubbleFavs, VsiH5 } from "../styled/App.styled";
import { Col, Row, CardHeader, Card, CardBody, Spinner } from "reactstrap";
import PieGrid from "../components/PieGrid";
import { useDataWithRoute } from "../utils/hooks";
import { useVsiAppContext } from "../stores/VsiApp";
import { genericColors, returnPieGridData } from "../utils/chartUtils";

import { formatStockChartConfig } from "../utils/Helpers";
import Stockchart from "../components/Stockchart";
import DonutChart from "../components/DonutChart";

const pieGridConfig = {
  clusters: {
    chartKeys: [
      "days_left_to_capacity",
      "total_hosts",
      "capacity_constrain_str"
    ],
    agKey: "num_clusters",
    titles: ["Weeks Left to Capacity", "Total Hosts", "Capacity Constraint"]
  },
  datastores: {
    chartKeys: ["vcenter_name", "cluster_name", "num_vms"],
    titles: ["VCenter", "Cluster", "Total VM/LPARs"],
    agKey: "num_datastore"
  },
  hosts: {
    chartKeys: ["cpu_util_pct", "memory_util_pct"],
    titles: ["CPU Utilization", "Memory Utilization"],
    agKey: "num_hosts"
  },
  vms: {
    chartKeys: ["os", "power_state", "disk_provision_type"],
    titles: ["Operating System", "Power State", "Provisioning"],
    agKey: "num_vms"
  }
};


const trendConfig = { yAxis: { title: { text: "# of VMs" } } };

const allUnits = [
  { value: "clusters", label: "Clusters", route: "enterprise_virtual_all_clusters" },
  { value: "datastores", label: "Datastores", route: "enterprise_virtual_all_datastores" },
  { value: "hosts", label: "Hosts", route: "enterprise_virtual_all_esx_hosts" },
  { value: "vms", label: "VMs", route: "enterprise_virtual_all_vms" }
];

const VmCharts = ({ allVmData }) => {
  const height = 450;
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("VIRTUAL");

  const route = useMemo(() => `/api/v6/report/enterprise_virtual_vms_trend_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordset: vmTrendData }] = useDataWithRoute(route);

  const calcVmTrends = useCallback(filteredData => {
    const trends = [];
    const dates = [];
    const vmNameLookup = {};
    const fullDateAndUtilizationOrganization = {};

    if (filteredData?.length) {
      filteredData.forEach(datum => {
        if (!vmNameLookup[datum.vm_name]) {
          vmNameLookup[datum.vm_name] = true;
        }
      });
    }

    vmTrendData.forEach(trendDatum => {
      if (!filteredData || vmNameLookup[trendDatum.vm_name]) {
        trends.push(trendDatum);
        if (!dates.includes(trendDatum.collection_date)) {
          dates.push(trendDatum.collection_date);
        }
        if (!fullDateAndUtilizationOrganization.hasOwnProperty(trendDatum.collection_date)) {
          fullDateAndUtilizationOrganization[trendDatum.collection_date] = {
            over_utilized_vms: 0,
            inrange_utilized_vms: 0,
            under_utilized_vms: 0,
          };
        }
        if (trendDatum.vsi_capacity_score >= .65) {
          fullDateAndUtilizationOrganization[trendDatum.collection_date].over_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].over_utilized_vms + 1;
        } else if (trendDatum.vsi_capacity_score < .65 && trendDatum.vsi_capacity_score > .15) {
          fullDateAndUtilizationOrganization[trendDatum.collection_date].inrange_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].inrange_utilized_vms + 1;
        } else if (trendDatum.vsi_capacity_score <= .15) {
          fullDateAndUtilizationOrganization[trendDatum.collection_date].under_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].under_utilized_vms + 1;
        }
      }
    });

    const trendsNew = dates.map(d => {
      return {
        collection_date: d,
        over_utilized_vms: fullDateAndUtilizationOrganization[d].over_utilized_vms,
        inrange_utilized_vms: fullDateAndUtilizationOrganization[d].inrange_utilized_vms,
        under_utilized_vms: fullDateAndUtilizationOrganization[d].under_utilized_vms,
      };
    });

    return trendsNew?.sort?.((a, b) => Date.parse(a.collection_date?.split?.('T')?.[0]) - Date.parse(b.collection_date?.split?.('T')?.[0]));
  }, [vmTrendData]);

  const formattedVmTrend = useMemo(() => {
    const formatted = formatStockChartConfig(calcVmTrends(allVmData),
      [
        { name: "Over Utilized (> 64%)", field: "over_utilized_vms" },
        { name: "In Range (16 - 64%)", field: "inrange_utilized_vms" },
        { name: "Under Utilized (< 15%)", field: "under_utilized_vms" }
      ]);
    return formatted.series;
  }, [vmTrendData, calcVmTrends, allVmData]);

  const totalCostDonut = useMemo(() => {
    return returnPieGridData(allVmData ?? [], ["monthly_total_cost"], ["num_vms"]);
  }, [allVmData]);

  return <>
    <Col xs={12} lg={8}>
      <VsiBubble>
        <Stockchart series={formattedVmTrend}
                    mutable={true}
                    title={`VMs Utilization Trends`}
                    height={height}
                    config={trendConfig} />
      </VsiBubble>
    </Col>
    <Col xs={12} lg={4}>
      <VsiBubble>
        <PieGrid
          title={`# VMs by Cost`}
          data={totalCostDonut?.[0] ?? []}
          gridConfig={["Cost", `# of VMs`]} />
      </VsiBubble>
    </Col>

  </>;
};

const HostCharts = ({ config, donuts, trendData }) => {

  const { theme } = useVsiAppContext();

  const formattedVmTrend = useMemo(() => {
    const formatted = formatStockChartConfig(trendData || [],
      [
        { name: "# Hosts > 70% CPU", field: "num_hosts_over_cpu" },
        { name: "# Hosts > 90% Memory", field: "num_hosts_over_memory" }
      ]);
    return formatted.series;
  }, [trendData]);

  return (
    <>
      <Col xs={12} lg={4}>
        <Card color={theme}>
          <CardHeader>
            <VsiH5>{`Hosts by ${config?.titles?.[0]}`}</VsiH5>
          </CardHeader>
          <CardBody>
            <PieGrid data={donuts?.[0] || []}
                     gridConfig={[config?.titles?.[0] || "", `# of Hosts`]} />
          </CardBody>
        </Card>
      </Col>
      <Col xs={12} lg={4}>
        <Stockchart series={formattedVmTrend}
                    config={trendConfig} />
      </Col>
      <Col xs={12} lg={4}>
        <Card color={theme}>
          <CardHeader>
            <VsiH5>{`Hosts by ${config?.titles?.[1]}`}</VsiH5>
          </CardHeader>
          <CardBody>
            <PieGrid data={donuts?.[1] || []}
                     gridConfig={[config?.titles?.[1] || "", `# of Hosts`]} />
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default function VirtualAllUnits2({ params }) {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("VIRTUAL");
  const {
    unit: {
      value: unitValue,
      label: unitLabel,
      route: unitRoute
    }
  } = useMemo(() => {
    const searchUnit = params && params.length > 0 && params[0]["value"] ? params[0]["value"] : "clusters";
    const unit = searchUnit ? allUnits.find(({ value }) => searchUnit === value) : allUnits[0];
    return {
      unit
    };
  }, [params]);

  const route = useMemo(() => `/api/v6/report/${unitRoute}_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId, unitRoute]);
  const [{ recordsets }, { isLoading }] = useDataWithRoute(route);
  const [rawChartData, rawHostData] = recordsets ?? [[], []];

  const { vmChartData, chartData } = useMemo(() => {
    const config = pieGridConfig[unitValue];

    return {
      vmChartData: rawChartData,
      chartData: returnPieGridData(rawChartData || [], config?.chartKeys, config?.agKey)
    };
  }, [unitValue, rawChartData]);

  const charts = useMemo(() => {
    const config = pieGridConfig?.[unitValue];

    if (unitValue === "vms") {
      return <VmCharts allVmData={vmChartData ?? []} />;
    } else if (unitValue === "hosts") {
      return <HostCharts donuts={chartData}
                         config={config}
                         trendData={rawHostData || []} />;
    } else {
      return chartData.map((chart, index) => {
        return (
          <Col md={12} lg={4} key={`donut-${index}`}>
            <VsiBubble>
              <DonutChart key={`pie-grid-all-units-virtual-${index}`}
                          data={chart.filter(datum => (datum.name !== "Total")).sort((a, b) => a.name === "Total" ? 1 : b.name === "Total" ? -1 : -1)}
                          alignTitle={"left"}
                          title={config?.titles?.[index]}
                          height={350} innerSize={80}
                          colors={genericColors}
                          seriesName={[config?.titles?.[index] || "", `# of ${unitLabel}`][1] || "Value"} />
            </VsiBubble>
          </Col>
        );
      });
    }
  }, [vmChartData, chartData, unitValue, unitLabel, rawHostData]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <Row>
      {charts}
    </Row>
  );
}
